import type { DayRange } from '../../lib/format/dates';
import { get, patch, post } from '../../lib/api/client';
import {
  checkAuditExportSentinel,
  downloadExport,
  exportFilename,
  fetchExport,
  saveBlob,
  type SentinelCheck,
} from '../../lib/api/download';
import { fromP2, type P2Response, type Page } from '../../lib/api/paging';
import type { Coverage, SettingSource } from '../../components';

/**
 * Money dashboards, alerts, chef payouts and reports (BE §7.8–§7.11). Dates on
 * these endpoints are YYYY-MM-DD in UTC (not the admin's timezone), and from/to
 * are half-open and sent together. Money is integer cents; rates are decimal
 * fractions unless a field says otherwise.
 */

// --- locale health ------------------------------------------------------------------------

export interface LocaleHealthCity {
  cityId: string;
  city: string;
  stateProvince: string;
  isHomeRule: boolean;
  coverage: Coverage;
  /** Decimal fraction. */
  effectiveBufferPct: number;
  bufferSource: SettingSource | '';
  bufferAccruedCents: number;
  unconfigured: boolean;
  transactionCount: number;
  grossCents: number;
}

export interface LocaleHealthRow {
  localeId: string;
  localeName: string;
  orders: number;
  grossCents: number;
  taxCollectedCents: number;
  feesCollectedCents: number;
  revenueFeeCents: number;
  bufferAccruedCents: number;
  openAlertCount: number;
  /** The locale has its own settings row (even if every field still inherits). */
  hasSettings: boolean;
  /** The price cap in force; above zero, pricing is on. */
  maxPriceCents: number;
  cities: LocaleHealthCity[];
}

/**
 * Which locales are listed. `orders`: those with orders in the range. The others
 * list dish locales whether or not they have orders, zero-filled: `pricing` —
 * price cap above $0; `pricing_off` — price cap at $0; `configured` — has its
 * own settings; `unconfigured` — has none.
 */
export type HealthView = 'orders' | 'pricing' | 'pricing_off' | 'configured' | 'unconfigured';

export interface LocaleHealth {
  range: { name: string; from: string; to: string };
  rows: LocaleHealthRow[] | null;
}

/** `days`: inclusive UTC days (the standard range control's window). */
export function getLocaleHealth(p: { days: DayRange; localeIds?: string[]; view?: HealthView }, signal?: AbortSignal) {
  return get<LocaleHealth>(
    '/admin/dashboards/locale-health',
    {
      startDate: p.days.start,
      endDate: p.days.end,
      localeIds: p.localeIds?.length ? p.localeIds.join(',') : undefined,
      view: p.view && p.view !== 'orders' ? p.view : undefined,
    },
    signal,
  );
}

// --- nexus ------------------------------------------------------------------------------------

export interface NexusRow {
  stateProvince: string;
  registered: boolean;
  registrationStatus: 'active' | 'none';
  registeredSince?: string;
  rolling12moGrossCents: number;
  rolling12moTxnCount: number;
  thresholdSalesCents?: number;
  thresholdTxns?: number;
  /** 0–100 scale; absent when no threshold is on file. */
  pctConsumed?: number;
  consumedBy?: 'sales' | 'transactions';
  projectedCrossing?: string;
  activeLocales?: string[];
}

export interface Nexus {
  rows: NexusRow[] | null;
  statesWithNoThresholdOnFile?: string[];
  /**
   * Activity over the requested days (rolling 12 months when none are sent).
   * The rows and their threshold % stay rolling 12 months — that is the nexus test.
   */
  window: { from: string; to: string; statesWithActivity: number; grossCents: number; txnCount: number };
}

/** `days`: inclusive UTC days for the activity figures. */
export const getNexus = (days: DayRange, signal?: AbortSignal) =>
  get<Nexus>('/admin/dashboards/nexus', { startDate: days.start, endDate: days.end }, signal);

// --- Colorado home rule ----------------------------------------------------------------------

export interface HomeRuleCity {
  cityId: string;
  city: string;
  stateProvince: string;
  registered: boolean;
  registrationStatus: 'active' | 'none';
  registeredSince?: string;
  transactionCount: number;
  grossCents: number;
  taxableBaseCents: number;
  taxCollectedCents: number;
  effectiveBufferPct: number;
  bufferSource: SettingSource | '';
  bufferAccruedCents: number;
  /** Decimal fraction despite the name. Absent: registered, or no rate on file. */
  cityRatePct?: number;
  estimatedExposureCents?: number;
  /** 1.0 = the buffer covers the exposure. */
  coverageRatio?: number;
  shortfallCents?: number;
}

export interface HomeRule {
  rows: HomeRuleCity[] | null;
  totals: { estimatedExposureCents: number; bufferAccruedCents: number; shortfallCents: number };
  citiesWithNoRateOnFile?: string[];
}

export const getHomeRule = (signal?: AbortSignal) => get<HomeRule>('/admin/dashboards/co-home-rule', undefined, signal);

// --- payouts aging ------------------------------------------------------------------------------

export type BucketKey = 'current' | '30' | '60' | '90' | '180' | '365' | '365+';
export const BUCKETS: BucketKey[] = ['current', '30', '60', '90', '180', '365', '365+'];
export type Rail = 'dots' | 'stripe_connect' | 'manual';
export const RAILS: Rail[] = ['dots', 'stripe_connect', 'manual'];

export interface AgingRow {
  profileId: string;
  displayName: string;
  localeId: string;
  lifetimeRecognizedCents: number;
  paidToDateCents: number;
  inFlightCents: number;
  spentOnPlatformCents: number;
  adjustmentNetCents: number;
  balanceCents: number;
  cachedBalanceCents: number;
  inSync: boolean;
  oldestUndrawnCreditAt: string | null;
  oldestUndrawnDays: number;
  railLastUsed: Rail | 'none';
  neverPaidOut: boolean;
  bscCandidate: boolean;
  bucketCents: Partial<Record<BucketKey, number>>;
}

export interface PayoutsAging {
  rows: AgingRow[] | null;
  totalBalanceCents: number;
  totalRecognizedCents: number;
  totalPaidCents: number;
  bucketTotals: Partial<Record<BucketKey, number>>;
  neverPaidOut: string[] | null;
  bscNotOnConnect: string[] | null;
  railSplit: Partial<Record<Rail | 'none', number>>;
  cacheExceptions: Array<{ profileId: string; cachedCents: number; ledgerCents: number }> | null;
}

export function getPayoutsAging(localeIds: string[], signal?: AbortSignal) {
  return get<PayoutsAging>(
    '/admin/dashboards/payouts-aging',
    { localeIds: localeIds.length ? localeIds.join(',') : undefined },
    signal,
  );
}

// --- chef payouts & ledger ------------------------------------------------------------------

export type PayoutStatus = 'pending' | 'in_flight' | 'paid' | 'failed';

export interface ChefPayout {
  id: string;
  createdAt: string;
  chefProfileId: string;
  localeId: string;
  amountCents: number;
  rail: Rail;
  status: PayoutStatus;
  requestedAt: string;
  sentAt?: string;
  paidAt?: string;
  externalPayoutId: string;
  failureReason: string;
}

export interface LedgerRow {
  id: string;
  createdAt: string;
  profileId: string;
  localeId: string;
  direction: 'credit' | 'debit';
  /** Always positive; direction carries the sign. */
  amountCents: number;
  reason: 'sale' | 'adjustment_credit' | 'payout' | 'order_spend' | 'adjustment_debit';
  orderId?: string;
  chefPayoutId?: string;
  balanceAfterCents: number;
  note: string;
}

export async function listChefPayouts(
  p: { chefProfileId?: string; status?: PayoutStatus; pageSize: number; offset: number },
  signal?: AbortSignal,
): Promise<Page<ChefPayout>> {
  return fromP2(await get<P2Response<ChefPayout>>('/admin/chef-payouts', { ...p }, signal));
}

export function createChefPayout(body: { chefProfileId: string; amountCents: number; rail: Rail }) {
  return post<{ payout: ChefPayout; debit: LedgerRow; balanceAfterCents: number }>('/admin/chef-payouts', body);
}

export function patchChefPayout(
  id: string,
  body: { status?: PayoutStatus; externalPayoutId?: string; failureReason?: string },
) {
  return patch<{ payout: ChefPayout; balanceCents: number }>(`/admin/chef-payouts/${id}`, body);
}

export interface ListerBalance {
  profileId: string;
  cachedBalanceCents: number;
  ledgerBalanceCents: number;
  inSync: boolean;
  oldestUndrawnCreditAt: string | null;
  items: LedgerRow[] | null;
  meta: { total: number; pageSize: number; offset: number };
}

export function getListerBalance(profileId: string, p: { pageSize: number; offset: number }, signal?: AbortSignal) {
  return get<ListerBalance>(`/admin/listers/${profileId}/balance`, { ...p }, signal);
}

export function adjustListerBalance(body: {
  profileId: string;
  amountCents: number;
  note: string;
  direction: 'credit' | 'debit';
}) {
  return post<LedgerRow>('/admin/lister-balance/adjustment', body);
}

// --- reconciliation --------------------------------------------------------------------------

export interface ReconciliationLine {
  orderId: string;
  snapshotId: string;
  settledOn?: string;
  chargeTotalCents: number;
  estimatedStripeFeeCents: number;
  reportedStripeFeeCents?: number;
  feeVarianceCents?: number;
  stripePayoutId?: string;
  unreconciled: boolean;
  reason?: string;
}

export interface Reconciliation {
  settlementDate?: string;
  transactionCount: number;
  chargeTotalCents: number;
  estimatedStripeFeeCents: number;
  reportedStripeFeeCents: number;
  feeVarianceCents: number;
  expectedNetCents: number;
  payoutTotalCents: number;
  varianceCents: number;
  payoutIds?: string[];
  unreconciledCount: number;
  ties: boolean;
  lines: ReconciliationLine[] | null;
}

/** Can fire alert #8 on a break — call only on an explicit Run. */
export function runReconciliation(settlementDate: string) {
  return get<Reconciliation>('/admin/dashboards/reconciliation', { settlementDate });
}

// --- filing calendar ---------------------------------------------------------------------------

export interface FilingRow {
  registrationId: string;
  entity: string;
  jurisdictionType: string;
  jurisdictionRef: string;
  jurisdictionName?: string;
  filingFrequency: string;
  nextDueDate?: string;
  whoFiles: string;
  status: string;
  /** The period the return due on nextDueDate is for; half-open, UTC. Absent without a due date. */
  periodStart?: string;
  periodEnd?: string;
  periodFiled: boolean;
  filedAt?: string;
  confirmationNumber?: string;
  overdue: boolean;
  daysUntilDue?: number;
}

export interface FilingCalendar {
  rows: FilingRow[] | null;
  totals: { activeRegistrations: number; overdue: number };
}

export const getFilingCalendar = (signal?: AbortSignal) =>
  get<FilingCalendar>('/admin/dashboards/filing-calendar', undefined, signal);

export interface MarkFiledInput {
  /** YYYY-MM-DD, UTC; not in the future. */
  filedOn: string;
  confirmationNumber: string;
  notes: string;
}

/**
 * Marks the return due on the registration's next due date filed. The server
 * picks the period (the one that return is for) and records the caller as the
 * filer.
 */
export const markFiled = (registrationId: string, body: MarkFiledInput) =>
  post<unknown>(`/admin/registrations/${registrationId}/mark-filed`, body);

// --- alerts ----------------------------------------------------------------------------------------

export interface AlertEvent {
  id: string;
  alertCode: number;
  triggeredAt: string;
  triggerContext?: Record<string, unknown>;
  recurringGroupKey: string;
  recipients: string[] | null;
  notifiedAt?: string;
  acknowledgedById?: string;
  acknowledgedAt?: string;
  acknowledgeNote?: string;
}

export interface AlertDef {
  code: number;
  name: string;
  description: string;
  severity: 'info' | 'warn' | 'critical';
  enabled: boolean;
}

export async function listAlerts(
  p: { status?: 'open' | 'acknowledged'; code?: number; pageSize: number; offset: number },
  signal?: AbortSignal,
): Promise<Page<AlertEvent>> {
  return fromP2(await get<P2Response<AlertEvent>>('/admin/alerts', { ...p }, signal));
}

export async function getAlertRegistry(signal?: AbortSignal): Promise<AlertDef[]> {
  return (await get<{ items: AlertDef[] | null }>('/admin/alerts/registry', undefined, signal)).items ?? [];
}

export const acknowledgeAlert = (id: string, note?: string) =>
  post<AlertEvent>(`/admin/alerts/${id}/acknowledge`, { note });

// --- reports ---------------------------------------------------------------------------------------

export interface FilingExtractRow {
  period: string;
  stateProvince: string;
  city: string;
  postalCode: string;
  isHomeRule?: boolean;
  cityRegisteredAtCollection: boolean;
  transactionCount: number;
  grossCents: number;
  taxableBaseCents: number;
  nonTaxableCents: number;
  taxCents: number;
  rdfCents: number;
  bufferCents: number;
}

export interface JurisdictionRow {
  level: string;
  displayName: string;
  stateProvince: string;
  country: string;
  transactionCount: number;
  taxCents: number;
  taxableCents: number;
  percentageDecimal?: string;
}

export interface FilingExtract {
  period: string;
  rows: FilingExtractRow[] | null;
  totals: FilingExtractRow;
  jurisdictions: JurisdictionRow[] | null;
  jurisdictionCoverage: {
    snapshotsInPeriod: number;
    snapshotsWithDetail: number;
    taxCentsInPeriod: number;
    taxCentsAttributed: number;
    complete: boolean;
  };
}

export interface ReportWindow {
  /** YYYY-MM or YYYY; or from+to (YYYY-MM-DD, UTC, half-open). */
  period?: string;
  from?: string;
  to?: string;
}

export function getFilingExtract(p: ReportWindow & { state?: string }, signal?: AbortSignal) {
  return get<FilingExtract>('/admin/reports/filing-extract', { ...p }, signal);
}

export const exportFilingExtract = (p: ReportWindow & { state?: string }) =>
  downloadExport('/admin/reports/filing-extract', 'filing-extract', { ...p, format: 'csv' });

export interface BufferLedgerRow {
  id: string;
  accruedDate: string;
  localeId: string;
  localeName: string;
  cityId?: string;
  cityName?: string;
  source: 'transaction' | 'adjustment';
  accruedCents: number;
  drawdownCents: number;
  drawdownReason?: string;
  runningBalanceCents: number;
}

export interface BufferLedger {
  rows: BufferLedgerRow[] | null;
  totals: { accruedCents: number; drawdownCents: number; balanceCents: number };
}

export function getBufferLedger(p: { locale?: string; from?: string; to?: string }, signal?: AbortSignal) {
  return get<BufferLedger>('/admin/reports/buffer-ledger', { ...p }, signal);
}

export const exportBufferLedger = (p: { locale?: string; from?: string; to?: string }) =>
  downloadExport('/admin/reports/buffer-ledger', 'buffer-ledger', { ...p, format: 'csv' });

export function drawDownBuffer(body: { localeId: string; cityId?: string; amountCents: number; reason: string }) {
  return post<{ remainingBufferCents: number }>('/admin/buffer-ledger/drawdown', body);
}

export interface RevenueRow {
  period: string;
  localeId: string;
  localeName: string;
  transactionCount: number;
  grossCents: number;
  revenueCents: number;
  avgRevenueCents: number;
  minRevenueCents: number;
  maxRevenueCents: number;
  /** 0–100 scale. */
  takeRatePct: number;
  outOfBandCount: number;
}

export interface RevenueOutlier {
  orderId: string;
  createdAt: string;
  localeName: string;
  revenueCents: number;
  expectedLowCents: number;
  expectedHighCents: number;
  effectiveTaxRateAtCalc: number;
  roundUpIncrementAtCalc: number;
  minRevenueAtCalc: number;
}

export function getRevenue(p: ReportWindow & { locale?: string; outliers?: boolean }, signal?: AbortSignal) {
  return get<{ rows: RevenueRow[] | null; outliers?: RevenueOutlier[] }>(
    '/admin/reports/revenue',
    { period: p.period, from: p.from, to: p.to, locale: p.locale, outliers: p.outliers ? '1' : undefined },
    signal,
  );
}

export const exportRevenue = (p: ReportWindow & { locale?: string; outliers?: boolean }) =>
  downloadExport('/admin/reports/revenue', p.outliers ? 'revenue-outliers' : 'revenue', {
    period: p.period,
    from: p.from,
    to: p.to,
    locale: p.locale,
    outliers: p.outliers ? '1' : undefined,
    format: 'csv',
  });

/**
 * Audit export (§7.11): every transaction snapshot in [from, to), streamed as
 * CSV. Saved whatever happens, then checked against the `# END,<n>` sentinel —
 * a streamed 200 can still be missing its back half.
 */
export async function downloadAuditExport(p: { from: string; to: string }): Promise<SentinelCheck> {
  const blob = await fetchExport('/admin/reports/transactions/audit-export', { ...p, format: 'csv' });
  saveBlob(blob, exportFilename(`audit-export-${p.from}-to-${p.to}`, 'csv'));
  return checkAuditExportSentinel(await blob.text());
}
